import * as riskApi from '../api/risk';
import { KeyedAsyncCache } from './asyncCache';

/** Mirrors RiskScoringService's own FACTOR_WEIGHTS exactly (backend
 * risk-scoring.service.ts) — the backend doesn't return a precomputed
 * compositeScore from a bare profile read, so it's recomputed here from the
 * same factors using the same weights. */
const FACTOR_WEIGHTS: Record<keyof riskApi.BackendFiveCFactors, number> = {
  capacity: 0.3,
  character: 0.25,
  capital: 0.15,
  collateral: 0.15,
  conditions: 0.15,
};

export function computeCompositeScore(factors: riskApi.BackendFiveCFactors): number {
  return (Object.keys(FACTOR_WEIGHTS) as Array<keyof riskApi.BackendFiveCFactors>).reduce(
    (sum, key) => sum + factors[key] * FACTOR_WEIGHTS[key],
    0,
  );
}

// `null` (fallback, distinct from a genuine "no profile yet" response) means
// "not fetched yet" here since a real API `null` is itself meaningful —
// callers check `=== undefined` for loading vs `=== null` for no profile.
const profileByFarmer = new KeyedAsyncCache<riskApi.BackendRiskProfile | null | undefined>(
  (farmerId) => riskApi.getRiskProfile(farmerId),
  undefined,
  'risk:profileByFarmer',
);

export function getFarmerRiskProfile(farmerId?: string): riskApi.BackendRiskProfile | null | undefined {
  if (!farmerId) return undefined;
  return profileByFarmer.get(farmerId);
}

const htxAverageCache = new KeyedAsyncCache<riskApi.HtxAverageRiskScore | undefined>(
  (htxId) => riskApi.getHtxAverageRiskScore(htxId),
  undefined,
  'risk:htxAverage',
);

export function getHtxAverageRiskScore(htxId?: string): riskApi.HtxAverageRiskScore | undefined {
  if (!htxId) return undefined;
  return htxAverageCache.get(htxId);
}

const signalTimelineByFarmer = new KeyedAsyncCache<riskApi.BackendCreditSignal[]>(
  (farmerId) => riskApi.getRiskSignalTimeline(farmerId),
  [],
  'risk:signalTimelineByFarmer',
);

export function getFarmerRiskSignals(farmerId?: string): riskApi.BackendCreditSignal[] {
  if (!farmerId) return [];
  return signalTimelineByFarmer.get(farmerId);
}
